import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";

const API = window.location.origin + "/api";

const EditRestaurantDialog = ({ restaurant, onOpenChange, onSaved }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (restaurant) {
      setForm({
        name: restaurant.name || "",
        city: restaurant.city || restaurant.location || "",
        country: restaurant.country || "",
        stars: String(restaurant.stars || 1),
        latitude: restaurant.latitude ?? "",
        longitude: restaurant.longitude ?? "",
        cuisine_type: restaurant.cuisine_type || "",
        price_range: restaurant.price_range || "",
        reservation_url: restaurant.reservation_url || ""
      });
    }
  }, [restaurant]);

  if (!restaurant || !form) return null;

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = async () => {
    if (!form.name.trim() || !form.city.trim() || !form.country.trim()) {
      toast.error("Name, Stadt und Land dürfen nicht leer sein");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        city: form.city.trim(),
        country: form.country.trim(),
        stars: parseInt(form.stars, 10),
        latitude: form.latitude !== "" ? parseFloat(form.latitude) : null,
        longitude: form.longitude !== "" ? parseFloat(form.longitude) : null,
        cuisine_type: form.cuisine_type.trim() || null,
        price_range: form.price_range || null,
        reservation_url: form.reservation_url.trim() || null
      };
      await axios.put(`${API}/admin/restaurants/${restaurant.restaurant_id}`, payload, { withCredentials: true });
      toast.success(`„${payload.name}" wurde aktualisiert`);
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (error) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!restaurant} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl" data-testid="edit-restaurant-dialog">
        <DialogHeader>
          <DialogTitle>Restaurant bearbeiten</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
          <div className="space-y-2 sm:col-span-2">
            <Label>Name *</Label>
            <Input data-testid="edit-restaurant-name-input" value={form.name} onChange={set("name")} className="bg-transparent border-border" />
          </div>
          <div className="space-y-2">
            <Label>Stadt *</Label>
            <Input data-testid="edit-restaurant-city-input" value={form.city} onChange={set("city")} className="bg-transparent border-border" />
          </div>
          <div className="space-y-2">
            <Label>Land *</Label>
            <Input data-testid="edit-restaurant-country-input" value={form.country} onChange={set("country")} className="bg-transparent border-border" />
          </div>
          <div className="space-y-2">
            <Label>Michelin-Sterne</Label>
            <Select value={form.stars} onValueChange={(v) => setForm({ ...form, stars: v })}>
              <SelectTrigger data-testid="edit-restaurant-stars-select" className="bg-transparent border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">★ 1 Stern</SelectItem>
                <SelectItem value="2">★★ 2 Sterne</SelectItem>
                <SelectItem value="3">★★★ 3 Sterne</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Küche</Label>
            <Input data-testid="edit-restaurant-cuisine-input" value={form.cuisine_type} onChange={set("cuisine_type")} className="bg-transparent border-border" />
          </div>
          <div className="space-y-2">
            <Label>Breitengrad</Label>
            <Input data-testid="edit-restaurant-latitude-input" type="number" step="any" value={form.latitude} onChange={set("latitude")} className="bg-transparent border-border" />
          </div>
          <div className="space-y-2">
            <Label>Längengrad</Label>
            <Input data-testid="edit-restaurant-longitude-input" type="number" step="any" value={form.longitude} onChange={set("longitude")} className="bg-transparent border-border" />
          </div>
          <div className="space-y-2">
            <Label>Preisklasse</Label>
            <Select value={form.price_range} onValueChange={(v) => setForm({ ...form, price_range: v })}>
              <SelectTrigger data-testid="edit-restaurant-price-select" className="bg-transparent border-border">
                <SelectValue placeholder="Optional" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="€€">€€</SelectItem>
                <SelectItem value="€€€">€€€</SelectItem>
                <SelectItem value="€€€€">€€€€</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Reservierungs-Link</Label>
            <Input data-testid="edit-restaurant-url-input" value={form.reservation_url} onChange={set("reservation_url")} placeholder="https://..." className="bg-transparent border-border" />
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="edit-restaurant-save-btn" className="mt-2 bg-primary text-primary-foreground hover:bg-primary/90">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Speichern
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default EditRestaurantDialog;
